<template>
	<article v-if="data" class="shorts-content">
		<div class="shorts-content__stage">
			<video
				ref="videoRef"
				class="shorts-content__video"
				:src="videoUrl"
				:poster="data.cover"
				playsinline
				autoplay
				loop
				@click="togglePlay"
				@play="onVideoPlay"
				@playing="onVideoPlaying"
				@waiting="onVideoWaiting"
				@timeupdate="onVideoTimeUpdate"
				@loadedmetadata="handleLoadedMetadata"
			></video>

			<img
				src="../../../images/back-shorts.png"
				alt=""
				class="shorts-content__back"
				@click="onBackClick"
			/>

			<div class="shorts-content__actions">
				<button
					type="button"
					class="shorts-content__action"
					:disabled="squareZanLoading"
					@click.stop="handleZan"
				>
					<div class="shorts-content__action-icon">
						<img
							:src="isZaned ? zanActiveIcon : zanBeforeIcon"
							alt=""
							:class="{ 'shorts-content__action-icon-img--active': isZaned }"
						/>
					</div>
					<span>{{ t("square.detail.like") }}</span>
				</button>
				<button type="button" class="shorts-content__action" @click.stop="onCommentClick">
					<div class="shorts-content__action-icon">
						<img :src="pinlunIcon" alt="" />
					</div>
					<span>{{ t("square.detail.comments") }}</span>
				</button>
				<button type="button" class="shorts-content__action" @click.stop="showSharePanel = true">
					<div class="shorts-content__action-icon">
						<img :src="shareIcon" alt="" />
					</div>
					<span>{{ t("square.detail.share") }}</span>
				</button>
			</div>

			<div class="shorts-content__overlay">
				<div class="shorts-content__author">
					<img
						:src="publisherInfo.avatar"
						:alt="publisherInfo.nickname"
						class="shorts-content__avatar"
					/>
					<span class="shorts-content__nickname">@{{ publisherInfo.nickname }}</span>
					<button
						type="button"
						class="shorts-content__follow-btn"
						:class="{ 'shorts-content__follow-btn--followed': isFollowed }"
						@click.stop="handleFollow"
					>
						{{ isFollowed ? t("square.detail.followed") : t("square.detail.follow") }}
					</button>
				</div>
				<p
					class="shorts-content__caption"
					:class="{ 'shorts-content__caption--expanded': captionExpanded }"
					@click="toggleCaption"
				>
					{{ captionText }}
					<button
						v-if="captionExpanded"
						type="button"
						class="shorts-content__collapse"
						@click.stop="captionExpanded = false"
					>
						{{ t("square.detail.shorts.collapse") }}
					</button>
				</p>
			</div>

			<div class="shorts-content__progress">
				<div
					class="shorts-content__progress-bar"
					:class="{ 'shorts-content__progress-bar--loading': isVideoLoading }"
					:style="isVideoLoading ? undefined : { width: `${progressPercent}%` }"
				></div>
			</div>
		</div>

		<div class="shorts-content__bottom-bar">
			<div class="shorts-content__input-wrap">
				<img :src="writeIcon" alt="" class="shorts-content__write-icon" aria-hidden="true" />
				<input
					v-model="mainCommentText"
					type="text"
					:placeholder="t('square.detail.commentPlaceholder')"
					maxlength="500"
					:disabled="commentSubmitting"
					@focus="openComments"
					@keyup.enter="submitMainComment"
				/>
				<button
					type="button"
					class="shorts-content__send-btn"
					:disabled="commentSubmitting || !mainCommentText.trim()"
					@click="submitMainComment"
				>
					<img :src="mainCommentText.trim() ? submit2 : submit1" alt="" />
				</button>
			</div>
		</div>

		<CommentDrawer
			v-model:show="showCommentDrawer"
			v-model:comment-tab="commentTab"
			v-model:main-comment-text="mainCommentText"
			v-model:active-reply-draft="activeReplyDraft"
			:square-data="data"
			:comment-list="commentList"
			:comment-loading="commentLoading"
			:comment-total="commentTotal"
			:has-more-comments="hasMoreComments"
			:active-reply-id="activeReplyId"
			:comment-submitting="commentSubmitting"
			:reply-submitting="replySubmitting"
			:get-reply-state="getReplyState"
			:has-more-replies="hasMoreReplies"
			:toggle-view-replies="toggleViewReplies"
			:load-more-replies="loadMoreReplies"
			:load-more-comments="loadMoreComments"
			:toggle-reply-input="toggleReplyInput"
			:submit-main-comment="submitMainComment"
			:submit-reply-comment="submitReplyComment"
			:handle-comment-zan="handleCommentZan"
			:is-comment-zan-loading="isCommentZanLoading"
		/>

		<SharePanel v-model:show="showSharePanel" :share-list="shareList" @share="handleShare" />
	</article>
</template>

<script setup>
import { computed, inject, ref, watch } from "vue";
import { useI18n } from "~/composables/useI18n";
import { useSquare } from "~/composables/useSquare";
import { safeNavigateBack } from "~/utils/safeNavigateBack";
import CommentDrawer from "../comment-drawer/index.vue";
import SharePanel from "../share-panel/index.vue";
import {
	bucketCommentCount,
	buildDetailContentParams,
	usePlayProgressTracking,
} from "../../../composables/useSquareDetailTracking";
import zanActiveIcon from "../../../images/zan_red.png";
import zanBeforeIcon from "../../../images/zan-before.png";
import pinlunIcon from "../../../images/pinlun.png";
import shareIcon from "../../../images/sahre.png";
import writeIcon from "../../../images/write.svg";
import submit1 from "../../../images/submit1.png";
import submit2 from "../../../images/submit2.png";

const props = defineProps({
	data: {
		type: Object,
		default: () => ({}),
	},
	squareId: {
		type: String,
		default: "",
	},
});

const { t, locale } = useI18n();
const logDetailEvent = inject("logDetailEvent", null);

const commentTab = ref("hot");
const showCommentDrawer = ref(false);
const captionExpanded = ref(false);
const videoRef = ref(null);
const progressPercent = ref(0);
const isVideoLoading = ref(true);
const playStartTracked = ref(false);

const {
	handleTimeUpdate,
	trackPlayStart,
	reset: resetPlayProgress,
} = usePlayProgressTracking(logDetailEvent, () => props.data);

watch(
	() => props.data?.id,
	() => {
		playStartTracked.value = false;
		isVideoLoading.value = true;
		progressPercent.value = 0;
		resetPlayProgress();
	}
);

const {
	isFollowed,
	isZaned,
	squareZanLoading,
	shareList,
	showSharePanel,
	handleFollow,
	handleZan,
	handleShare,
	commentList,
	commentLoading,
	commentTotal,
	hasMoreComments,
	mainCommentText,
	activeReplyId,
	activeReplyDraft,
	commentSubmitting,
	replySubmitting,
	getReplyState,
	hasMoreReplies,
	toggleViewReplies,
	loadMoreReplies,
	loadMoreComments,
	toggleReplyInput,
	submitMainComment,
	submitReplyComment,
	handleCommentZan,
	isCommentZanLoading,
} = useSquare(
	() => props.data,
	() => commentTab.value
);

const publisherInfo = computed(
	() =>
		props.data?.publisher_info || {
			nickname: "",
			avatar: "",
		}
);

const captionText = computed(() => {
	if (!props.data) return "";
	const title = props.data.title || "";
	const desc =
		locale.value === "en"
			? props.data.content_en || props.data.desc || props.data.content || ""
			: props.data.content || props.data.desc || props.data.content_en || "";
	return desc ? `${title} | ${desc}` : title;
});

const showCaptionToggle = computed(() => captionText.value.length > 48);

const getVideoUrl = mediaUrl => {
	if (!mediaUrl) return "";
	if (Array.isArray(mediaUrl) && mediaUrl.length > 0) {
		return String(mediaUrl[0]).replace(/\\\//g, "/");
	}
	if (typeof mediaUrl === "string") {
		try {
			const parsed = JSON.parse(mediaUrl);
			if (Array.isArray(parsed) && parsed.length > 0) {
				return String(parsed[0]).replace(/\\\//g, "/");
			}
		} catch {
			return mediaUrl.replace(/\\\//g, "/");
		}
	}
	return "";
};

const videoUrl = computed(() => getVideoUrl(props.data?.media_url));

const openComments = () => {
	showCommentDrawer.value = true;
};

const onCommentClick = () => {
	if (logDetailEvent) {
		logDetailEvent(
			"short_comment_click",
			buildDetailContentParams(props.data, {
				comment_count_bucket: bucketCommentCount(props.data?.comment_count),
			})
		);
	}
	openComments();
};

const toggleCaption = () => {
	if (!showCaptionToggle.value || captionExpanded.value) return;
	captionExpanded.value = true;
};

const onBackClick = () => {
	if (logDetailEvent) {
		logDetailEvent(
			"short_navigation_control_click",
			buildDetailContentParams(props.data, { control_type: "back" })
		);
	}
	goBack();
};

const goBack = () => {
	safeNavigateBack("/seer/square/shorts");
};

const togglePlay = () => {
	const video = videoRef.value;
	if (!video) return;
	if (video.paused) {
		video.play();
	} else {
		video.pause();
	}
};

const onVideoPlay = () => {
	if (playStartTracked.value) return;
	playStartTracked.value = true;
	trackPlayStart({ autoplay: true });
};

const onVideoPlaying = () => {
	isVideoLoading.value = false;
};

const onVideoWaiting = () => {
	const video = videoRef.value;
	if (!video || video.currentTime === 0) {
		isVideoLoading.value = true;
	}
};

const onVideoTimeUpdate = () => {
	const video = videoRef.value;
	if (!video || !video.duration) return;
	if (isVideoLoading.value && video.currentTime > 0) {
		isVideoLoading.value = false;
	}
	progressPercent.value = (video.currentTime / video.duration) * 100;
	handleTimeUpdate(video);
};

const handleLoadedMetadata = () => {
	progressPercent.value = 0;
};
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
}

.shorts-content {
	position: fixed;
	top: 0;
	left: 50%;
	transform: translateX(-50%);
	z-index: 20;
	width: 750px;
	height: 100vh;
	height: 100dvh;
	background: #000000;
	font-family: "PingFang SC", sans-serif;
	overflow: hidden;

	&__stage {
		position: relative;
		width: 100%;
		height: 100%;
	}

	&__video {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
		background: #000000;
	}

	&__back {
		position: absolute;
		top: 30px;
		left: 0;
		z-index: 3;
		width: 750px;
		height: 100px;
		cursor: pointer;
	}

	&__actions {
		position: absolute;
		right: 20px;
		bottom: 460px;
		z-index: 3;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 28px;
	}

	&__action {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 6px;
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;
		color: #ffffff;
		font-size: 28px;
		line-height: 1;
		font-weight: 500;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}
	}

	&__action-icon {
		width: 80px;
		height: 80px;
		padding: 14px;
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.5);
		box-sizing: border-box;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
			filter: brightness(0) invert(1);
		}
	}

	&__action-icon-img--active {
		filter: none !important;
	}

	&__overlay {
		position: absolute;
		left: 0;
		right: 88px;
		bottom: 120px;
		z-index: 2;
		padding: 0 24px 16px;
		background: linear-gradient(to top, rgba(0, 0, 0, 0.72) 0%, rgba(0, 0, 0, 0) 100%);
		pointer-events: none;

		> * {
			pointer-events: auto;
		}
	}

	&__author {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-bottom: 12px;
	}

	&__avatar {
		width: 64px;
		height: 64px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
	}

	&__nickname {
		min-width: 0;
		font-size: 32px;
		font-weight: 500;
		color: #ffffff;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__follow-btn {
		padding: 8px 20px;
		border: 1px solid #ffffff;
		border-radius: 8px;
		background: transparent;
		color: #ffffff;
		font-size: 24px;
		cursor: pointer;
		white-space: nowrap;
		margin-left: 10px;

		&--followed {
			background: #ffffff;
			color: #333333;
		}
	}

	&__caption {
		margin: 20px 0;
		font-size: 28px;
		color: #ffffff;
		line-height: 1.5;
		@include line-clamp(2);
		cursor: pointer;

		&--expanded {
			display: block;
			-webkit-line-clamp: unset;
			max-height: 42vh;
			overflow-y: auto;
		}
	}

	&__collapse {
		border: none;
		background: transparent;
		padding: 0;
		margin-left: 8px;
		color: rgba(255, 255, 255, 0.75);
		font-size: 26px;
		cursor: pointer;
	}

	&__progress {
		position: absolute;
		left: 0;
		right: 0;
		bottom: calc(132px + env(safe-area-inset-bottom));
		z-index: 5;
		height: 3px;
		overflow: hidden;
		background: rgba(255, 255, 255, 0.2);
	}

	&__progress-bar {
		height: 100%;
		background: #ff4757;
		transition: width 0.1s linear;

		&--loading {
			position: absolute;
			top: 0;
			left: 0;
			width: 35%;
			transition: none;
			animation: shorts-content-progress-loading 1.2s ease-in-out infinite;
		}
	}

	&__bottom-bar {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 4;
		padding: 30px 24px calc(30px + env(safe-area-inset-bottom));
		background: #000000;
	}

	&__input-wrap {
		display: flex;
		align-items: center;
		gap: 12px;
		height: 72px;
		padding: 0 12px 0 20px;
		background: #595959;
		border-radius: 36px;

		input {
			flex: 1;
			min-width: 0;
			height: 100%;
			border: none;
			background: transparent;
			font-size: 28px;
			color: #ffffff;
			outline: none;

			&::placeholder {
				color: rgba(255, 255, 255, 0.55);
			}

			&:disabled {
				cursor: not-allowed;
			}
		}
	}

	&__write-icon {
		width: 28px;
		height: 28px;
		object-fit: contain;
		flex-shrink: 0;
		filter: brightness(0) invert(1);
		opacity: 0.7;
	}

	&__send-btn {
		flex-shrink: 0;
		width: 48px;
		height: 48px;
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;

		&:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}
}

@keyframes shorts-content-progress-loading {
	0% {
		left: -35%;
	}

	100% {
		left: 100%;
	}
}
</style>
